import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface DashboardStats {
  totalUsers: number;
  planDistribution: Record<string, number>;
  totalMessagesSent: number;
  messagesThisMonth: number;
  messagesToday: number;
  activeCampaigns: number;
  totalContacts: number;
  usersWithWhatsApp: number;
  whatsappsConnected: number;
  newUsers30Days: number;
  queuePending: number;
  queueFailed: number;
  onboardingCompleted: number;
  mrr: number;
  activeClients: number;
  trialClients: number;
  revenue12m: Array<{ month: string; amount: number }>;
  newUsers12m: Array<{ month: string; count: number }>;
  recentUsers: Array<{
    id: string;
    full_name: string;
    email: string;
    plan: string;
    role: string;
    created_at: string;
  }>;
  expiringTrials: Array<{
    id: string;
    full_name: string;
    email: string;
    plan: string;
    created_at: string;
  }>;
}

const PLAN_COLORS: Record<string, string> = {
  trial: 'bg-secondary-500',
  starter: 'bg-accent-500',
  professional: 'bg-primary-500',
  business: 'bg-amber-500',
  enterprise: 'bg-emerald-600',
};

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business',
  enterprise: 'Enterprise',
};

const PLAN_BADGES: Record<string, string> = {
  trial: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-500/10 dark:text-secondary-400',
  starter: 'bg-accent-100 text-accent-700 dark:bg-accent-500/10 dark:text-accent-400',
  professional: 'bg-primary-100 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400',
  business: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  enterprise: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
};

// Mock infrastructure data
const infraMock = {
  cpu: { usage: 23, cores: 4, freq: '2.0 GHz' },
  ram: { used: 4.2, total: 8, unit: 'GB' },
  ssd: { used: 42, total: 160, unit: 'GB' },
};

const serviceStatusMock = [
  { name: 'Evolution API', status: 'online', version: 'v2.2.1' },
  { name: 'PostgreSQL', status: 'online', version: 'v16.4' },
  { name: 'Redis', status: 'online', version: 'v7.2.5' },
  { name: 'Docker', status: 'online', version: 'v26.1.3' },
  { name: 'Nginx', status: 'online', version: 'v1.26.1' },
  { name: 'SSL', status: 'valid', expires: '87 dias' },
  { name: 'Backup', status: 'ok', last: 'Hoje 03:00' },
  { name: 'Updates', status: 'pending', count: 4 },
];

const monthLabels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function AdminDashboardPage() {
  const { profile } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkPlan, setBulkPlan] = useState('starter');
  const [applying, setApplying] = useState(false);
  const [downgrading, setDowngrading] = useState(false);
  const [tab, setTab] = useState<'overview' | 'charts'>('overview');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-dashboard-stats', { method: 'GET' });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setStats(data as DashboardStats);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (profile && profile.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const toggleUser = (userId: string) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const selectAllTrials = () => {
    if (!stats) return;
    setSelectedUsers(new Set(stats.recentUsers.filter((u) => u.plan === 'trial').map((u) => u.id)));
  };
  const clearSelection = () => setSelectedUsers(new Set());

  const handleBulkUpdate = async () => {
    if (selectedUsers.size === 0) return;
    setApplying(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-dashboard-stats', {
        method: 'POST', body: { action: 'bulk_update_plan', userIds: Array.from(selectedUsers), newPlan: bulkPlan },
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setSuccessMsg(`${data.updated} usuário(s) movido(s) para ${PLAN_LABELS[bulkPlan] || bulkPlan}!`);
      setSelectedUsers(new Set()); fetchStats();
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erro ao atualizar'); }
    finally { setApplying(false); setTimeout(() => setSuccessMsg(''), 4000); }
  };

  const handleDowngradeExpired = async () => {
    setDowngrading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-dashboard-stats', {
        method: 'POST', body: { action: 'downgrade_expired_trials' },
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setSuccessMsg(`${data.downgraded || 0} trial(s) expirado(s) processado(s).`);
      fetchStats();
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erro ao processar'); }
    finally { setDowngrading(false); setTimeout(() => setSuccessMsg(''), 4000); }
  };

  const formatNumber = (n: number) => n.toLocaleString('pt-BR');
  const formatCurrency = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

  const maxPlanCount = stats ? Math.max(...Object.values(stats.planDistribution), 1) : 1;
  const maxRevenue = stats ? Math.max(...stats.revenue12m.map((r) => r.amount), 1) : 1;
  const maxNewUsers = stats ? Math.max(...stats.newUsers12m.map((u) => u.count), 1) : 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-28">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-foreground-500">Carregando métricas do sistema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">Dashboard Admin</h1>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">Visão geral da plataforma, infraestrutura e métricas</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-background-100 rounded-full p-1">
            <button onClick={() => setTab('overview')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${tab === 'overview' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
              Visão Geral
            </button>
            <button onClick={() => setTab('charts')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${tab === 'charts' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
              Gráficos
            </button>
          </div>
          <button onClick={fetchStats} className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-refresh-line text-base" />
          </button>
        </div>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-check-line text-emerald-500 text-lg" /><p className="text-sm text-emerald-700 dark:text-emerald-400">{successMsg}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" /><p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {stats && tab === 'overview' && (
        <>
          {/* Plan Distribution Quick View */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 dark:text-foreground-100">Distribuição de Planos</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Controle de planos e assinaturas dos usuários</p>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/admin/plans" className="px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-vip-crown-line mr-1" />Gerenciar planos
                </Link>
                <Link to="/admin/users" className="px-3 py-1.5 text-xs font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-team-line mr-1" />Ver usuários
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(PLAN_LABELS).map(([planKey, planLabel]) => {
                const count = stats.planDistribution[planKey] || 0;
                const pct = stats.totalUsers > 0 ? Math.round((count / stats.totalUsers) * 100) : 0;
                const colorMap: Record<string, string> = {
                  trial: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-500/10 dark:text-secondary-400',
                  starter: 'bg-accent-100 text-accent-700 dark:bg-accent-500/10 dark:text-accent-400',
                  professional: 'bg-primary-100 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400',
                  business: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
                  enterprise: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
                };
                const barColorMap: Record<string, string> = {
                  trial: 'bg-secondary-500',
                  starter: 'bg-accent-500',
                  professional: 'bg-primary-500',
                  business: 'bg-amber-500',
                  enterprise: 'bg-emerald-600',
                };
                return (
                  <div key={planKey} className="bg-background-50 dark:bg-background-200 rounded-xl p-3 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${colorMap[planKey] || 'bg-background-100 text-foreground-500'}`}>
                        {planLabel}
                      </span>
                      <span className="text-xs text-foreground-400">{pct}%</span>
                    </div>
                    <p className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100">{count}</p>
                    <div className="w-full h-1.5 bg-background-200 dark:bg-background-300 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${barColorMap[planKey] || 'bg-foreground-400'}`} style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                    <p className="text-[10px] text-foreground-400">usuário{count !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Row 1: Revenue & Clients */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              icon="ri-money-dollar-circle-line" iconBg="bg-emerald-100 dark:bg-emerald-500/10" iconColor="text-emerald-600 dark:text-emerald-400"
              label="Receita Mensal (MRR)" value={formatCurrency(stats.mrr)} sub={`${stats.activeClients} clientes ativos`}
            />
            <MetricCard
              icon="ri-user-star-line" iconBg="bg-accent-100 dark:bg-accent-500/10" iconColor="text-accent-600 dark:text-accent-400"
              label="Clientes Ativos" value={formatNumber(stats.activeClients)} sub="Planos pagos"
            />
            <MetricCard
              icon="ri-user-heart-line" iconBg="bg-secondary-100 dark:bg-secondary-500/10" iconColor="text-secondary-600 dark:text-secondary-400"
              label="Em Teste" value={formatNumber(stats.trialClients)} sub="Período trial"
            />
            <MetricCard
              icon="ri-whatsapp-line" iconBg="bg-green-100 dark:bg-green-500/10" iconColor="text-green-600 dark:text-green-400"
              label="WhatsApps Conectados" value={formatNumber(stats.whatsappsConnected)} sub={`${stats.usersWithWhatsApp} empresas`}
            />
          </div>

          {/* Row 2: Messages & Campaigns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              icon="ri-send-plane-line" iconBg="bg-primary-100 dark:bg-primary-500/10" iconColor="text-primary-600 dark:text-primary-400"
              label="Mensagens Hoje" value={formatNumber(stats.messagesToday)} sub="Enviadas hoje"
            />
            <MetricCard
              icon="ri-send-plane-fill" iconBg="bg-primary-50 dark:bg-primary-500/5" iconColor="text-primary-600 dark:text-primary-400"
              label="Mensagens no Mês" value={formatNumber(stats.messagesThisMonth)} sub="Total mensal"
            />
            <MetricCard
              icon="ri-megaphone-line" iconBg="bg-amber-100 dark:bg-amber-500/10" iconColor="text-amber-600 dark:text-amber-400"
              label="Campanhas Ativas" value={stats.activeCampaigns.toString()} sub={`${formatNumber(stats.totalContacts)} contatos`}
            />
            <MetricCard
              icon="ri-stack-line" iconBg="bg-amber-50 dark:bg-amber-500/5" iconColor="text-amber-600 dark:text-amber-400"
              label="Fila de Processamento" value={formatNumber(stats.queuePending)} sub={`${stats.queueFailed} falhas`} subColor="text-red-500"
            />
          </div>

          {/* Row 3: Infrastructure */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <h3 className="text-sm font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">Infraestrutura</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* CPU */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground-600"><i className="ri-cpu-line mr-1.5" />CPU</span>
                  <span className="text-xs font-bold text-foreground-900">{infraMock.cpu.usage}%</span>
                </div>
                <div className="h-2.5 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${infraMock.cpu.usage > 80 ? 'bg-red-500' : infraMock.cpu.usage > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${infraMock.cpu.usage}%` }} />
                </div>
                <p className="text-[10px] text-foreground-400 mt-1">{infraMock.cpu.cores} vCPUs @ {infraMock.cpu.freq}</p>
              </div>
              {/* RAM */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground-600"><i className="ri-hard-drive-line mr-1.5" />RAM</span>
                  <span className="text-xs font-bold text-foreground-900">{infraMock.ram.used}/{infraMock.ram.total} {infraMock.ram.unit}</span>
                </div>
                <div className="h-2.5 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${(infraMock.ram.used / infraMock.ram.total) > 0.8 ? 'bg-red-500' : (infraMock.ram.used / infraMock.ram.total) > 0.6 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${(infraMock.ram.used / infraMock.ram.total) * 100}%` }} />
                </div>
                <p className="text-[10px] text-foreground-400 mt-1">DDR4 ECC</p>
              </div>
              {/* SSD */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground-600"><i className="ri-hard-drive-2-line mr-1.5" />SSD</span>
                  <span className="text-xs font-bold text-foreground-900">{infraMock.ssd.used}/{infraMock.ssd.total} {infraMock.ssd.unit}</span>
                </div>
                <div className="h-2.5 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${(infraMock.ssd.used / infraMock.ssd.total) * 100}%` }} />
                </div>
                <p className="text-[10px] text-foreground-400 mt-1">NVMe Gen4</p>
              </div>
            </div>
          </div>

          {/* Row 4: Service Status */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <h3 className="text-sm font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">Status dos Serviços</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {serviceStatusMock.map((svc) => (
                <div key={svc.name} className="flex items-center gap-3 p-3 rounded-xl border border-background-200 hover:border-emerald-200 transition-colors">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${svc.status === 'online' || svc.status === 'valid' || svc.status === 'ok' ? 'bg-emerald-500' : svc.status === 'pending' ? 'bg-amber-500' : 'bg-red-500'}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground-900">{svc.name}</p>
                    <p className={`text-[10px] font-medium ${svc.status === 'pending' ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {svc.status === 'pending' ? `${svc.count} pendentes` : svc.status === 'valid' ? `Válido (${svc.expires})` : svc.status === 'ok' ? `Último: ${svc.last}` : svc.version}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expiring Trials */}
          {stats.expiringTrials && stats.expiringTrials.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/15 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-500/10 flex items-center justify-center">
                    <i className="ri-alarm-warning-line text-lg text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-bold text-amber-800 dark:text-amber-300">Trials Prestes a Expirar</h3>
                    <p className="text-xs text-amber-600 dark:text-amber-400">{stats.expiringTrials.length} usuário(s) com trial criado há 6-7 dias</p>
                  </div>
                </div>
                <button onClick={handleDowngradeExpired} disabled={downgrading} className="px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                  {downgrading ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Processando...</> : <><i className="ri-arrow-down-line text-sm" />Baixar Trials Expirados</>}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.expiringTrials.map((u) => (
                  <span key={u.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-background-100 rounded-lg border border-amber-200 dark:border-amber-500/10 text-xs">
                    <span className="font-medium text-foreground-700">{u.full_name || u.email}</span>
                    <span className="text-amber-500">· expira em breve</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Bulk Plan Update */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 dark:text-foreground-100">Atualização em Lote</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Selecione usuários e aplique um novo plano</p>
              </div>
              {selectedUsers.size > 0 && (
                <div className="flex items-center gap-2">
                  <select value={bulkPlan} onChange={(e) => setBulkPlan(e.target.value)} className="px-3 py-2 bg-white dark:bg-background-100 border border-background-200 rounded-lg text-sm outline-none focus:border-primary-400 cursor-pointer">
                    {Object.entries(PLAN_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <button onClick={handleBulkUpdate} disabled={applying} className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    {applying ? 'Aplicando...' : `Aplicar (${selectedUsers.size})`}
                  </button>
                  <button onClick={clearSelection} className="px-3 py-2 text-sm font-medium text-foreground-500 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Limpar</button>
                </div>
              )}
            </div>
            <button onClick={selectAllTrials} className="px-2.5 py-1 text-xs font-medium bg-secondary-100 dark:bg-secondary-500/10 text-secondary-700 dark:text-secondary-400 rounded-lg hover:bg-secondary-200 cursor-pointer whitespace-nowrap mb-3">Selecionar todos Trials</button>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 dark:border-background-200">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase w-10"><input type="checkbox" onChange={(e) => { if (e.target.checked && stats) setSelectedUsers(new Set(stats.recentUsers.map((u) => u.id))); else clearSelection(); }} checked={stats ? selectedUsers.size === stats.recentUsers.length && stats.recentUsers.length > 0 : false} className="w-4 h-4 rounded cursor-pointer" /></th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase">Usuário</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase hidden sm:table-cell">Email</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase">Plano</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase hidden md:table-cell">Cargo</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase hidden lg:table-cell">Cadastro</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentUsers.map((u) => (
                    <tr key={u.id} className={`border-b border-background-50 dark:border-background-200 hover:bg-background-50/60 transition-colors ${selectedUsers.has(u.id) ? 'bg-primary-50/30 dark:bg-primary-500/5' : ''}`}>
                      <td className="py-2.5 px-3"><input type="checkbox" checked={selectedUsers.has(u.id)} onChange={() => toggleUser(u.id)} className="w-4 h-4 rounded cursor-pointer" /></td>
                      <td className="py-2.5 px-3"><span className="font-medium text-foreground-800 dark:text-foreground-200 text-[13px]">{u.full_name || 'Sem nome'}</span></td>
                      <td className="py-2.5 px-3 text-foreground-500 text-[13px] hidden sm:table-cell truncate max-w-[180px]">{u.email}</td>
                      <td className="py-2.5 px-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${PLAN_BADGES[u.plan] || 'bg-background-100 text-foreground-600'}`}>{PLAN_LABELS[u.plan] || u.plan}</span></td>
                      <td className="py-2.5 px-3 hidden md:table-cell"><span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-secondary-100 text-secondary-700'}`}>{u.role === 'admin' ? 'Admin' : u.role === 'manager' ? 'Gerente' : u.role === 'operator' ? 'Operador' : 'Usuário'}</span></td>
                      <td className="py-2.5 px-3 text-right text-foreground-500 text-[13px] hidden lg:table-cell">{formatDate(u.created_at)}</td>
                    </tr>
                  ))}
                  {stats.recentUsers.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-sm text-foreground-400">Nenhum usuário</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {stats && tab === 'charts' && (
        <div className="space-y-6">
          {/* Revenue 12 months */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <h3 className="text-sm font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">Receita (Últimos 12 Meses)</h3>
            <div className="flex items-end gap-2 h-[200px]">
              {stats.revenue12m.map((r) => {
                const pct = maxRevenue > 0 ? (r.amount / maxRevenue) * 100 : 0;
                const d = new Date(r.month + '-01');
                const label = monthLabels[d.getMonth()];
                return (
                  <div key={r.month} className="flex-1 flex flex-col items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-foreground-700 whitespace-nowrap">{r.amount > 0 ? formatCurrency(r.amount) : ''}</span>
                    <div className="w-full bg-primary-500 rounded-t-md transition-all duration-700" style={{ height: `${Math.max(pct, 3)}%`, minHeight: r.amount > 0 ? '8px' : '2px', opacity: r.amount > 0 ? 1 : 0.3 }} />
                    <span className="text-[10px] text-foreground-500">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* New clients 12 months */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <h3 className="text-sm font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">Novos Clientes (Últimos 12 Meses)</h3>
            <div className="flex items-end gap-2 h-[180px]">
              {stats.newUsers12m.map((u) => {
                const pct = maxNewUsers > 0 ? (u.count / maxNewUsers) * 100 : 0;
                const d = new Date(u.month + '-01');
                const label = monthLabels[d.getMonth()];
                return (
                  <div key={u.month} className="flex-1 flex flex-col items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-foreground-700">{u.count > 0 ? u.count : ''}</span>
                    <div className="w-full bg-accent-500 rounded-t-md transition-all duration-700" style={{ height: `${Math.max(pct, 3)}%`, minHeight: u.count > 0 ? '8px' : '2px', opacity: u.count > 0 ? 1 : 0.3 }} />
                    <span className="text-[10px] text-foreground-500">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Clients by plan */}
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
            <h3 className="text-sm font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">Clientes por Plano</h3>
            <div className="space-y-3">
              {Object.entries(stats.planDistribution).map(([plan, count]) => {
                const pct = stats.totalUsers > 0 ? Math.round((count / stats.totalUsers) * 100) : 0;
                const barPct = maxPlanCount > 0 ? (count / maxPlanCount) * 100 : 0;
                return (
                  <div key={plan} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-foreground-600 dark:text-foreground-400 w-[90px] whitespace-nowrap">{PLAN_LABELS[plan] || plan}</span>
                    <div className="flex-1 h-6 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-700 ${PLAN_COLORS[plan] || 'bg-foreground-400'}`} style={{ width: `${Math.max(barPct, 1)}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-foreground-700 dark:text-foreground-300 w-[70px] text-right">{count} <span className="text-foreground-400 font-normal">({pct}%)</span></span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon, iconBg, iconColor, label, value, sub, subColor }: {
  icon: string; iconBg: string; iconColor: string; label: string; value: string; sub: string; subColor?: string;
}) {
  return (
    <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
          <i className={`${icon} text-lg ${iconColor}`} />
        </div>
        <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-foreground-950 dark:text-foreground-100">{value}</p>
      <p className={`text-xs mt-1 ${subColor || 'text-foreground-500'}`}>{sub}</p>
    </div>
  );
}